import { useState } from 'react'

import { styled } from 'styled-components'

import { useParams } from 'peach/router'

import { authActions } from 'core/auth/authStore'
import Clickable from 'core/components/lib/Clickable'
import Icon from 'core/components/Icon'
import {
  STANDARD_ENVIRONMENTS,
  selectStandardEnvironment,
  setActiveId,
  useActiveEnv,
  useCustomEnvs,
} from 'core/stores/env'

import DefaultFallback from './DefaultFallback'

type WrongEnvErrorShape = { status?: number; message?: string }

export const isWrongEnvError = (
  error: unknown,
  companyId: string | undefined,
): boolean => {
  if (!error || typeof error !== 'object') return false
  const e = error as WrongEnvErrorShape
  const status = typeof e.status === 'number' ? e.status : undefined
  if (status !== undefined && (status < 400 || status >= 500)) return false
  const msg = typeof e.message === 'string' ? e.message : ''
  if (/invalid identifier/i.test(msg)) return true
  if (companyId && msg.includes(companyId)) return true
  return false
}

type Props = {
  error: unknown
  onReset: () => void
  componentStack?: string
}

const WrongEnvFallback = ({ error, onReset, componentStack }: Props) => {
  const { companyId } = useParams<{ companyId?: string }>()
  const activeEnv = useActiveEnv()
  const customEnvs = useCustomEnvs()
  const [switchingId, setSwitchingId] = useState<string | undefined>()

  const matched = isWrongEnvError(error, companyId)

  if (!matched) {
    return (
      <DefaultFallback
        componentStack={componentStack}
        error={error}
        onReset={onReset}
      />
    )
  }

  const switchEnv = async (type: 'standard' | 'custom', id: string) => {
    setSwitchingId(id)
    try {
      authActions.setInitializingAsTrue()
      await new Promise((r) => setTimeout(r, 100))
      if (type === 'standard') {
        selectStandardEnvironment(id)
      } else {
        setActiveId(id)
      }
      await new Promise((r) => setTimeout(r, 100))
      await authActions.initializeSessionAndUser(id)
      window.location.reload()
    } catch {
      setSwitchingId(undefined)
    }
  }

  const otherStandard = STANDARD_ENVIRONMENTS.filter(
    (e) => e.id !== activeEnv?.id,
  )
  const otherCustom = customEnvs.filter((e) => e.id !== activeEnv?.id)

  const isSwitching = switchingId !== undefined
  const activeLabel = activeEnv?.label ?? 'this environment'

  return (
    <Panel>
      <Title>
        {companyId
          ? `${companyId} not found in ${activeLabel}`
          : `Not found in ${activeLabel}`}
      </Title>
      <Sub>
        This link may belong to a different environment than the one you are
        currently signed in to. Select an environment below to switch and retry:
      </Sub>
      <EnvList>
        {otherStandard.length > 0 && (
          <EnvGroup>
            <EnvGroupLabel>Standard Environments</EnvGroupLabel>
            {otherStandard.map((env) => (
              <EnvButton
                disabled={isSwitching}
                key={`std-${env.id}`}
                onClick={() => switchEnv('standard', env.id)}
              >
                <EnvButtonLeft>
                  <EnvLabel>
                    {switchingId === env.id ? 'Switching…' : env.label}
                  </EnvLabel>
                  <EnvSub>{env.apiBase}</EnvSub>
                </EnvButtonLeft>
                <EnvArrow>
                  <Icon fontSize='16px' name='arrow_forward_ios' />
                </EnvArrow>
              </EnvButton>
            ))}
          </EnvGroup>
        )}
        {otherCustom.length > 0 && (
          <EnvGroup>
            <EnvGroupLabel>Custom Environments</EnvGroupLabel>
            {otherCustom.map((env) => (
              <EnvButton
                disabled={isSwitching}
                key={`custom-${env.id}`}
                onClick={() => switchEnv('custom', env.id)}
              >
                <EnvButtonLeft>
                  <EnvLabel>
                    {switchingId === env.id
                      ? 'Switching…'
                      : env.label || 'Custom Environment'}
                  </EnvLabel>
                  <EnvSub>{env.apiBase}</EnvSub>
                </EnvButtonLeft>
                <EnvArrow>
                  <Icon fontSize='16px' name='arrow_forward_ios' />
                </EnvArrow>
              </EnvButton>
            ))}
          </EnvGroup>
        )}
      </EnvList>
      <Footer>
        <Clickable onClick={() => window.location.reload()} variant='outline'>
          Reload Page
        </Clickable>
        <Clickable onClick={onReset} variant='outline'>
          Reload Component
        </Clickable>
      </Footer>
    </Panel>
  )
}

export default WrongEnvFallback

const Panel = styled.div`
  margin: 32px auto;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 8px;
  background: ${(p) => p.theme.background};
  padding: 24px;
  max-width: 640px;
  color: ${(p) => p.theme.text};
`

const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 20px;
`

const Sub = styled.p`
  opacity: 0.9;
  margin: 0 0 24px;
  color: ${(p) => p.theme.text};
`

const EnvList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const EnvArrow = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  transition: opacity 0.1s;
  opacity: 0;
  color: ${(p) => p.theme.text};
`

const EnvButtonLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const EnvButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.1s;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: 6px;
  background: ${(p) => p.theme.offset};
  cursor: pointer;
  padding: 10px 14px;
  width: 100%;
  text-align: left;
  color: ${(p) => p.theme.text};

  &:hover:not(:disabled) {
    background: ${(p) => p.theme.hover};
  }

  &:hover:not(:disabled) > ${EnvArrow} {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: progress;
  }
`

const EnvLabel = styled.span`
  font-weight: 500;
`

const EnvSub = styled.span`
  color: ${(p) => p.theme.mutedText};
  font-size: 12px;
`

const Footer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`

const EnvGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EnvGroupLabel = styled.span`
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(p) => p.theme.text};
  font-size: 11px;
  font-weight: 600;
`
